import apiClient from './client';
import { API_CONFIG } from '@/config/constants';
import { debug } from '@/utils/debug';
import type {
  GoogleAdsAccount,
  GoogleAdsLinkRequest,
  GoogleAdsLinkStatus
} from '@/types/api';

export interface HealthCheckResult {
  health_level: 'healthy' | 'warning' | 'critical' | 'suspended' | 'unknown';
  can_link: boolean;
  message: string;
  disapproval_rate?: number | null;
  disapproved_ads?: number | null;
  has_egregious_violations: boolean;
}

export interface GoogleAdsLinkResponse {
  success: boolean;
  message: string;
  customer_id: string;
  email_sent: boolean;
  health_check?: HealthCheckResult;
}

export interface GoogleAdsSetupResponse {
  success: boolean;
  message: string;
  customer_id: string;
  data_sync_triggered: boolean;
}

export const googleAdsApi = {
  /**
   * Get list of linked Google Ads accounts
   */
  async getLinkedAccounts(): Promise<GoogleAdsAccount[]> {
    debug.log('googleAdsApi: Getting linked accounts');
    const response = await apiClient.get<GoogleAdsAccount[]>('/auth/google/accounts');
    debug.log('googleAdsApi: Linked accounts response', response.data);
    return response.data;
  },

  /**
   * Send invitation to link Google Ads account
   */
  async linkAccount(request: GoogleAdsLinkRequest): Promise<GoogleAdsLinkResponse> {
    debug.log('googleAdsApi: Sending link invitation', request);

    const response = await apiClient.post<GoogleAdsLinkResponse>(
      '/google-ads/link-account',
      { customer_id: request.customer_id }
    );

    debug.log('googleAdsApi: Link invitation response', response.data);
    return response.data;
  },

  /**
   * Get detailed link status for a Google Ads account
   */
  async getLinkStatus(customerId: string): Promise<GoogleAdsLinkStatus> {
    debug.log('googleAdsApi: Getting link status', { customerId });

    const response = await apiClient.get<GoogleAdsLinkStatus>(
      `/google-ads/link-status/${customerId}`
    );

    debug.log('googleAdsApi: Link status response', response.data);
    return response.data;
  },

  /**
   * Complete Google Ads account setup after linking
   */
  async completeSetup(customerId: string): Promise<GoogleAdsSetupResponse> {
    debug.log('googleAdsApi: Completing account setup', { customerId });

    // Complete setup can take a long time due to data sync, use extended timeout
    const response = await apiClient.post<GoogleAdsSetupResponse>(
      '/google-ads/complete-setup',
      { customer_id: customerId },
      { timeout: API_CONFIG.COMPLETE_SETUP_TIMEOUT_MS }
    );

    debug.log('googleAdsApi: Setup completion response', response.data);
    return response.data;
  },

  /**
   * Test endpoint for alternative linking methods
   */
  async testLinkAccountV2(customerId: string): Promise<GoogleAdsLinkResponse> {
    debug.log('googleAdsApi: Sending test link invitation v2', { customerId });
    
    const response = await apiClient.post<GoogleAdsLinkResponse>(
      '/google-ads/test-link-v2',
      { customer_id: customerId }
    );
    
    debug.log('googleAdsApi: Test link invitation v2 response', response.data);
    return response.data;
  },

  /**
   * Debug MCC configuration
   */
  async debugMccConfig(): Promise<Record<string, unknown>> {
    debug.log('googleAdsApi: Getting MCC debug config');

    const response = await apiClient.get<Record<string, unknown>>(
      '/google-ads/debug/mcc-config'
    );

    debug.log('googleAdsApi: MCC debug config response', response.data);
    return response.data;
  },

  /**
   * Trigger Google Ads data sync to refresh campaign data
   */
  async triggerDataSync(memberUuid: string, daysBack: number = 30): Promise<Record<string, unknown>> {
    debug.log('googleAdsApi: Triggering data sync', { memberUuid, daysBack });

    const response = await apiClient.post<Record<string, unknown>>(
      '/data-sync/trigger',
      {
        member_uuid: memberUuid,
        days_back: daysBack
      }
    );

    debug.log('googleAdsApi: Data sync triggered', response.data);
    return response.data;
  },

  /**
   * Unlink a Google Ads account from the MCC
   */
  async unlinkAccount(customerId: string): Promise<{ success: boolean; message: string; customer_id: string }> {
    debug.log('googleAdsApi: Unlinking account', { customerId });

    const response = await apiClient.post<{ success: boolean; message: string; customer_id: string }>(
      `/google-ads/unlink-account/${customerId}`
    );

    debug.log('googleAdsApi: Unlink account response', response.data);
    return response.data;
  },

  /**
   * Batch update keyword CPC bids
   * @param memberUuid Member UUID
   * @param bidUpdates Array of keyword bid updates
   * @param skipDataSync Whether to skip triggering data sync (for batching with other operations)
   */
  async batchUpdateKeywordBids(
    memberUuid: string,
    bidUpdates: Array<{ keyword_id: string; new_bid: number }>,
    skipDataSync: boolean = false,
    customerId?: string
  ): Promise<{
    success: boolean;
    message: string;
    successful_updates: Array<{ keyword_id: string; new_bid: number }>;
    failed_updates: Array<{ keyword_id: string; new_bid: number; error: string }>;
  }> {
    debug.log('googleAdsApi: Batch updating keyword bids', { memberUuid, bidUpdates, skipDataSync, customerId });

    const response = await apiClient.post<{
      success: boolean;
      message: string;
      successful_updates: Array<{ keyword_id: string; new_bid: number }>;
      failed_updates: Array<{ keyword_id: string; new_bid: number; error: string }>;
    }>('/keywords/bids/batch-update', {
      member_uuid: memberUuid,
      bid_updates: bidUpdates,
      skip_data_sync: skipDataSync,
      customer_id: customerId
    });

    debug.log('googleAdsApi: Batch update response', response.data);
    return response.data;
  }
};